import { useEffect, useRef, useState } from 'react';
import { Search, Trash2, Plus, Link2, PencilLine, Upload, Check } from 'lucide-react';
import { CANONICAL_SUBSEGMENTS, ALL_MARKET_CODES, ALL_MARKET_NAMES } from '@hc/shared';
import { useBuyerHoldings, useCreateBuyerHolding, useDeleteBuyerHolding } from '../../api/hooks.js';
import { fetchScreener, type ScreenerRow } from '../../api/screener-client.js';
import { PanelFrame } from '../panel-frame.js';
import { clusterByGroup, GroupHeader, GROUP_MEMBER_INDENT } from '../entity-search-grouping.js';
import { extractCandidates, matchCandidates, type ImportCandidate } from '../portfolio-import.js';
import type { PanelId, TabId } from '../store.js';

// Portfolio — what the buyer already operates. Holdings are added by PICKING a DB
// entity (linked, live data), KEYING IN details (off-market), or UPLOADING a
// holdings file (xlsx/csv/pdf/…) that is parsed client-side, matched against the
// entity universe, and confirmed row-by-row before anything is written.
// Per-user, Clerk-isolated. Attached to Prescription to exclude owned assets +
// seed the anchor.

const MARKET_OPTS = [...ALL_MARKET_CODES]
  .map((c) => ({ code: c, name: ALL_MARKET_NAMES[c] }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Geocode a UK postcode → lat/lon via postcodes.io (public, CORS-enabled, no key
// — the same source the CQC sites use). Other markets: store the postcode, geocode
// when their geocoder lands. Returns null on any failure (postcode still saved).
async function geocodeUkPostcode(
  postcode: string,
): Promise<{ lat: number; lon: number; region: string | null } | null> {
  try {
    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim())}`,
    );
    if (!res.ok) return null;
    const j = (await res.json()) as {
      result?: { latitude?: number; longitude?: number; region?: string | null };
    };
    const r = j.result;
    if (r && typeof r.latitude === 'number' && typeof r.longitude === 'number') {
      return { lat: r.latitude, lon: r.longitude, region: r.region ?? null };
    }
    return null;
  } catch {
    return null;
  }
}

export function PortfolioPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { data } = useBuyerHoldings();
  const holdings = data?.data ?? [];
  const create = useCreateBuyerHolding();
  const del = useDeleteBuyerHolding();

  const [mode, setMode] = useState<'db' | 'manual' | 'file'>('db');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ScreenerRow[]>([]);
  // File-import flow: idle → parsing → matching → review → adding.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importPhase, setImportPhase] = useState<
    'idle' | 'parsing' | 'matching' | 'review' | 'adding'
  >('idle');
  const [importFile, setImportFile] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<[number, number]>([0, 0]);
  const [candidates, setCandidates] = useState<ImportCandidate[]>([]);
  const [manual, setManual] = useState({
    name: '',
    countryCode: 'GB',
    subsegment: '',
    region: '',
    postcode: '',
    revM: '',
    sites: '',
  });
  const [error, setError] = useState<string | null>(null);

  // Debounced entity search (reuses the screener's name-prefix `q`).
  useEffect(() => {
    if (mode !== 'db' || q.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetchScreener({ q: q.trim(), limit: 8 })
        .then((r) => {
          if (!cancelled) setResults(r.rows);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, mode]);

  const heldIds = new Set(holdings.map((h) => h.entityId).filter(Boolean));

  const addFromDb = (row: ScreenerRow) => {
    setError(null);
    create.mutate(
      { entityId: row.entityId },
      {
        onError: (e) =>
          setError(
            e instanceof Error && /409/.test(e.message)
              ? 'Already in your portfolio.'
              : 'Could not add.',
          ),
      },
    );
    setQ('');
    setResults([]);
  };

  const addManual = async () => {
    setError(null);
    if (!manual.name.trim()) {
      setError('Give the holding a name.');
      return;
    }
    // Geocode a UK postcode so the manual holding has a precise point for
    // catchment / bolt-on modelling; non-UK just stores the postcode for now.
    const postcode = manual.postcode.trim();
    const geo = postcode && manual.countryCode === 'GB' ? await geocodeUkPostcode(postcode) : null;
    create.mutate(
      {
        name: manual.name.trim(),
        countryCode: manual.countryCode || null,
        subsegment: manual.subsegment || null,
        // Prefer the typed region; else the geocoder's region (postcodes.io).
        region: manual.region.trim() || geo?.region || null,
        postcode: postcode || null,
        latitude: geo?.lat ?? null,
        longitude: geo?.lon ?? null,
        turnoverEur: manual.revM ? Math.round(Number(manual.revM) * 1_000_000) : null,
        sites: manual.sites ? Number(manual.sites) : null,
      },
      { onError: () => setError('Could not add.') },
    );
    setManual({
      name: '',
      countryCode: 'GB',
      subsegment: '',
      region: '',
      postcode: '',
      revM: '',
      sites: '',
    });
  };

  const resetImport = () => {
    setImportPhase('idle');
    setImportFile(null);
    setCandidates([]);
    setImportProgress([0, 0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (file: File) => {
    setError(null);
    setImportFile(file.name);
    setImportPhase('parsing');
    try {
      const names = await extractCandidates(file);
      if (names.length === 0) {
        setError('No company names found in that file.');
        resetImport();
        return;
      }
      setImportPhase('matching');
      setImportProgress([0, names.length]);
      const matched = await matchCandidates(names, heldIds as Set<string>, (done, total) =>
        setImportProgress([done, total]),
      );
      setCandidates(matched);
      setImportPhase('review');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.');
      resetImport();
    }
  };

  const toggleCandidate = (i: number) =>
    setCandidates((cs) => cs.map((c, j) => (j === i ? { ...c, selected: !c.selected } : c)));

  const addSelected = async () => {
    setImportPhase('adding');
    setError(null);
    let failed = 0;
    for (const c of candidates) {
      if (!c.selected || c.held) continue;
      try {
        await create.mutateAsync(
          c.match ? { entityId: c.match.entityId } : { name: c.raw, countryCode: null },
        );
      } catch {
        failed++;
      }
    }
    if (failed > 0) setError(`${failed} holding${failed === 1 ? '' : 's'} could not be added.`);
    resetImport();
  };

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="pf-root">
        <style>{PF_CSS}</style>

        <div className="pf-top">
          <span className="pf-title">Your portfolio</span>
          <span className="pf-sum">
            {holdings.length} {holdings.length === 1 ? 'holding' : 'holdings'}
          </span>
        </div>

        {error && <div className="pf-error">{error}</div>}

        <div className="pf-body">
          <p className="pf-intro">
            What you already operate. Add assets from the database (linked, live figures) or key in
            off-market ones. Attach this in Prescription to exclude what you own and seed the
            anchor.
          </p>

          {/* Add controls */}
          <div className="pf-add">
            <div className="pf-modes">
              <button
                type="button"
                className={'pf-mode' + (mode === 'db' ? ' on' : '')}
                onClick={() => setMode('db')}
              >
                <Link2 size={12} /> From database
              </button>
              <button
                type="button"
                className={'pf-mode' + (mode === 'manual' ? ' on' : '')}
                onClick={() => setMode('manual')}
              >
                <PencilLine size={12} /> Manual
              </button>
              <button
                type="button"
                className={'pf-mode' + (mode === 'file' ? ' on' : '')}
                onClick={() => setMode('file')}
              >
                <Upload size={12} /> From file
              </button>
            </div>

            {mode === 'file' ? (
              <div className="pf-import">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.ods,.csv,.tsv,.txt,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(f);
                  }}
                />
                {importPhase === 'idle' && (
                  <>
                    <button
                      type="button"
                      className="pf-addbtn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={13} /> Choose file…
                    </button>
                    <span className="pf-importhint">
                      xlsx, xls, csv, tsv, txt or pdf. Parsed in your browser — the file is not
                      uploaded. Names are matched against the database; you confirm before anything
                      is added.
                    </span>
                  </>
                )}
                {(importPhase === 'parsing' || importPhase === 'matching') && (
                  <span className="pf-importhint">
                    {importPhase === 'parsing'
                      ? `Reading ${importFile}…`
                      : `Matching against the database… ${importProgress[0]} / ${importProgress[1]}`}
                  </span>
                )}
                {(importPhase === 'review' || importPhase === 'adding') && (
                  <>
                    <div className="pf-importsum">
                      <span>
                        {importFile} — {candidates.length} name
                        {candidates.length === 1 ? '' : 's'} found,{' '}
                        {candidates.filter((c) => c.match && !c.held).length} matched
                      </span>
                      <button type="button" className="pf-import-cancel" onClick={resetImport}>
                        Cancel
                      </button>
                    </div>
                    <div className="pf-importlist">
                      {candidates.map((c, i) => (
                        <button
                          key={i}
                          type="button"
                          className="pf-import-row"
                          disabled={c.held || importPhase === 'adding'}
                          onClick={() => toggleCandidate(i)}
                        >
                          <span
                            className={'pf-import-check' + (c.selected && !c.held ? ' on' : '')}
                            aria-hidden
                          >
                            {c.selected && !c.held && <Check size={10} strokeWidth={3} />}
                          </span>
                          <span className="pf-import-name">{c.match?.companyName ?? c.raw}</span>
                          <span className="pf-import-meta">
                            {c.held
                              ? 'already held'
                              : c.match
                                ? `${c.match.countryCode} · ${c.match.subsegment ?? '—'}`
                                : 'not in database — off-market'}
                          </span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="pf-addbtn"
                      disabled={
                        importPhase === 'adding' ||
                        candidates.filter((c) => c.selected && !c.held).length === 0
                      }
                      onClick={() => void addSelected()}
                    >
                      <Plus size={13} />
                      {importPhase === 'adding'
                        ? 'Adding…'
                        : `Add ${candidates.filter((c) => c.selected && !c.held).length} to portfolio`}
                    </button>
                  </>
                )}
              </div>
            ) : mode === 'db' ? (
              <div className="pf-search">
                <div className="pf-searchbar">
                  <Search size={13} style={{ color: 'var(--text-tertiary)' }} />
                  <input
                    className="pf-input"
                    value={q}
                    placeholder="Search companies by name…"
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
                {results.length > 0 && (
                  <div className="pf-results">
                    {clusterByGroup(results, (r) => r.economicGroup).clusters.map((cluster) => (
                      <div key={cluster.group ?? `solo-${cluster.rows[0]!.entityId}`}>
                        {cluster.group && (
                          <GroupHeader group={cluster.group} count={cluster.rows.length} />
                        )}
                        {cluster.rows.map((r) => {
                          const held = heldIds.has(r.entityId);
                          return (
                            <button
                              key={r.entityId}
                              type="button"
                              className="pf-result"
                              style={
                                cluster.group
                                  ? { paddingLeft: `calc(var(--s-2) + ${GROUP_MEMBER_INDENT}px)` }
                                  : undefined
                              }
                              disabled={held}
                              onClick={() => addFromDb(r)}
                            >
                              <span className="pf-rname">{r.companyName ?? '—'}</span>
                              <span className="pf-rmeta">
                                {r.countryCode} · {r.subsegment ?? '—'}
                              </span>
                              {held ? <span className="pf-held">held</span> : <Plus size={12} />}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="pf-manual">
                <input
                  className="pf-input"
                  value={manual.name}
                  placeholder="Name *"
                  onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
                />
                <div className="pf-row">
                  <select
                    className="pf-input"
                    value={manual.countryCode}
                    onChange={(e) => setManual((m) => ({ ...m, countryCode: e.target.value }))}
                  >
                    {MARKET_OPTS.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="pf-input"
                    value={manual.subsegment}
                    onChange={(e) => setManual((m) => ({ ...m, subsegment: e.target.value }))}
                  >
                    <option value="">Sub-segment…</option>
                    {CANONICAL_SUBSEGMENTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pf-row">
                  <input
                    className="pf-input"
                    value={manual.region}
                    placeholder="Region"
                    onChange={(e) => setManual((m) => ({ ...m, region: e.target.value }))}
                  />
                  <input
                    className="pf-input"
                    value={manual.postcode}
                    placeholder={manual.countryCode === 'GB' ? 'Postcode (geocoded)' : 'Postcode'}
                    onChange={(e) => setManual((m) => ({ ...m, postcode: e.target.value }))}
                  />
                </div>
                <div className="pf-row">
                  <input
                    className="pf-input sm"
                    type="number"
                    min={0}
                    value={manual.revM}
                    placeholder="Rev €m"
                    onChange={(e) => setManual((m) => ({ ...m, revM: e.target.value }))}
                  />
                  <input
                    className="pf-input sm"
                    type="number"
                    min={0}
                    value={manual.sites}
                    placeholder="Sites"
                    onChange={(e) => setManual((m) => ({ ...m, sites: e.target.value }))}
                  />
                </div>
                <button type="button" className="pf-addbtn" onClick={() => void addManual()}>
                  <Plus size={13} /> Add holding
                </button>
              </div>
            )}
          </div>

          {/* Holdings list */}
          <div className="pf-list">
            {holdings.length === 0 && <div className="pf-empty">No holdings yet.</div>}
            {holdings.map((h) => (
              <div key={h.id} className="pf-holding">
                <div className="pf-hmain">
                  <span className="pf-hname">
                    {h.name ?? '—'}
                    {h.linked && <span className="pf-linked" title="Linked to a database entity" />}
                  </span>
                  <span className="pf-hmeta">
                    {[h.countryCode, h.subsegment, h.region].filter(Boolean).join(' · ') || '—'}
                  </span>
                </div>
                <button
                  type="button"
                  className="pf-del"
                  title="Remove"
                  onClick={() => del.mutate(h.id)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelFrame>
  );
}

const PF_CSS = `
.pf-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); }
.pf-top { display:flex; align-items:baseline; gap:var(--s-2); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); background:var(--surface); }
.pf-title { font-weight:700; color:var(--text-primary); }
.pf-sum { font-size:var(--t-meta); color:var(--text-tertiary); }
.pf-error { padding:6px var(--s-3); background:color-mix(in srgb, var(--negative,#c0143c) 12%, transparent); color:var(--negative,#c0143c); font-size:var(--t-meta); }
.pf-body { flex:1; overflow:auto; padding:var(--s-3); min-height:0; max-width:640px; display:flex; flex-direction:column; gap:var(--s-3); }
.pf-intro { margin:0; line-height:1.5; color:var(--text-tertiary); font-size:var(--t-meta); }
.pf-add { border:1px solid var(--hairline); border-radius:var(--radius-sm); padding:var(--s-2); display:flex; flex-direction:column; gap:var(--s-2); background:var(--surface); }
.pf-modes { display:flex; gap:4px; }
.pf-mode { display:inline-flex; align-items:center; gap:5px; font:inherit; font-size:var(--t-meta); padding:2px 8px; border:1px solid var(--hairline); border-radius:12px; background:var(--surface); color:var(--text-secondary); cursor:pointer; }
.pf-mode.on { background:var(--color-accent-100); border-color:var(--accent); color:var(--text-primary); font-weight:600; }
.pf-searchbar { display:flex; align-items:center; gap:6px; border:1px solid var(--hairline); border-radius:2px; padding:2px var(--s-2); }
.pf-input { font:inherit; font-size:var(--t-body); padding:4px var(--s-2); border:1px solid var(--hairline); border-radius:2px; background:var(--surface); color:var(--text-primary); width:100%; box-sizing:border-box; }
.pf-searchbar .pf-input { border:none; padding:2px 0; }
.pf-input.sm { width:90px; }
.pf-row { display:flex; gap:6px; }
.pf-manual { display:flex; flex-direction:column; gap:6px; }
.pf-results { display:flex; flex-direction:column; margin-top:4px; border:1px solid var(--hairline); border-radius:2px; overflow:hidden; }
.pf-result { display:flex; align-items:center; gap:8px; font:inherit; font-size:var(--t-body); text-align:left; padding:5px var(--s-2); border:none; border-bottom:1px solid var(--hairline); background:var(--surface); color:var(--text-primary); cursor:pointer; }
.pf-result:last-child { border-bottom:none; }
.pf-result:hover:not(:disabled) { background:var(--hover-subtle); }
.pf-result:disabled { opacity:0.5; cursor:default; }
.pf-rname { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pf-rmeta { font-size:var(--t-meta); color:var(--text-tertiary); flex-shrink:0; }
.pf-held { font-size:9px; text-transform:uppercase; color:var(--text-tertiary); }
.pf-addbtn { display:inline-flex; align-items:center; gap:5px; align-self:flex-start; font:inherit; font-size:var(--t-body); padding:3px var(--s-2); border:1px solid var(--accent); border-radius:var(--radius-sm); background:var(--accent); color:var(--bg); font-weight:600; cursor:pointer; }
.pf-addbtn:disabled { opacity:0.5; cursor:default; }
.pf-import { display:flex; flex-direction:column; gap:var(--s-2); }
.pf-importhint { font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.5; }
.pf-importsum { display:flex; align-items:center; justify-content:space-between; gap:var(--s-2); font-size:var(--t-meta); color:var(--text-secondary); }
.pf-import-cancel { font:inherit; font-size:var(--t-meta); color:var(--text-secondary); background:transparent; border:none; cursor:pointer; padding:2px 4px; }
.pf-import-cancel:hover { color:var(--text-primary); }
.pf-importlist { display:flex; flex-direction:column; max-height:260px; overflow:auto; border:1px solid var(--hairline); border-radius:var(--radius-sm); }
.pf-import-row { display:flex; align-items:center; gap:8px; font:inherit; font-size:var(--t-body); text-align:left; padding:5px var(--s-2); border:none; border-bottom:1px solid var(--hairline); background:var(--surface); color:var(--text-primary); cursor:pointer; }
.pf-import-row:last-child { border-bottom:none; }
.pf-import-row:hover:not(:disabled) { background:var(--hover-subtle); }
.pf-import-row:disabled { opacity:0.5; cursor:default; }
.pf-import-check { display:inline-flex; align-items:center; justify-content:center; width:14px; height:14px; flex-shrink:0; border-radius:var(--radius-sm); border:1px solid var(--hairline); background:var(--surface); color:var(--bg); }
.pf-import-check.on { border-color:var(--accent); background:var(--accent); }
.pf-import-name { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pf-import-meta { font-size:var(--t-meta); color:var(--text-tertiary); flex-shrink:0; }
.pf-list { display:flex; flex-direction:column; }
.pf-empty { color:var(--text-tertiary); font-size:var(--t-meta); padding:var(--s-2) 0; }
.pf-holding { display:flex; align-items:center; gap:var(--s-2); padding:6px 0; border-bottom:1px solid var(--hairline); }
.pf-hmain { flex:1; min-width:0; display:flex; flex-direction:column; }
.pf-hname { font-weight:600; color:var(--text-primary); display:flex; align-items:center; gap:6px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pf-linked { width:6px; height:6px; border-radius:50%; background:var(--positive,#0a7d5a); flex-shrink:0; }
.pf-hmeta { font-size:var(--t-meta); color:var(--text-tertiary); }
.pf-del { display:inline-flex; padding:3px; border:none; background:transparent; color:var(--text-tertiary); cursor:pointer; border-radius:2px; }
.pf-del:hover { color:var(--negative,#c0143c); background:var(--hover-subtle); }
`;
